import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getNewsById, getWorkById } from "@/utils/media";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = getNewsById(id);
  const relatedWork = item ? getWorkById(item.relatedWorkId) : undefined;

  useSeo({
    title: item ? `影视资讯：${item.title} - 挣扎吧亚当君` : "资讯未找到 - 挣扎吧亚当君",
    description: item ? item.summary : "抱歉，未找到该影视资讯。",
    keywords: item ? `${item.category},${item.title},影视资讯,挣扎吧亚当君` : "影视资讯",
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-file-unknow-line text-3xl"></i>
        </div>
        <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该资讯</h1>
        <p className="mt-2 text-sm text-foreground-500">文章可能已被移除或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = newsList.filter((n) => n.id !== item.id && n.category === item.category).slice(0, 3);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news" },
          { label: item.category },
        ]}
      />

      <article className="mt-6 max-w-4xl mx-auto">
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-800">
          {item.category}
        </span>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
          {item.title}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {item.date}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            {item.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-price-tag-3-line"></i>
            {item.source}
          </span>
        </div>

        <div className="mt-8 w-full aspect-[16/9] rounded-lg overflow-hidden bg-secondary-100">
          <img
            src={item.cover}
            alt={`影视资讯封面：${item.title}`}
            title={`${item.title} - 影视资讯`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <p className="mt-8 text-base md:text-lg text-foreground-800 font-medium leading-loose border-l-4 border-accent-500 pl-5">
          {item.summary}
        </p>

        <div className="mt-6 space-y-5">
          {item.content.map((p, i) => (
            <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
              {p}
            </p>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-background-200/80 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
          {relatedWork && (
            <Link
              to={`/detail/${relatedWork.id}`}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              阅读《{relatedWork.title}》剧情
              <i className="ri-arrow-right-line"></i>
            </Link>
          )}
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-14 max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关文章</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="group bg-background-50 rounded-lg border border-background-200/80 hover:border-primary-300 transition-colors p-5 cursor-pointer"
              >
                <span className="text-xs text-foreground-500">{n.date}</span>
                <h3 className="mt-2 font-heading text-sm font-bold text-foreground-950 leading-snug line-clamp-3 group-hover:text-primary-600 transition-colors">
                  {n.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}